import { useState, useEffect, useRef, lazy, Suspense } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { PageLoader } from '@cinqo/ui'
import { AuthProvider, useAuth } from './lib/auth'
import { supabase } from './lib/supabase'
import LoginPage from './components/LoginPage'
import LoginPageStaff from './components/LoginPageStaff'
import Shell from './components/Shell'
import { NewVersionBanner } from './components/NewVersionBanner'
import PdfViewerHost from './components/PdfViewerHost'

/* ── Lazy-loaded route components ─────────────────────────────────────
   Chaque page est compilee dans son propre chunk JS et telechargee a la
   demande par le navigateur. Divise le bundle initial par role:
   - un client ne telecharge pas le code regulateur/partenaire
   - un partenaire ne telecharge pas le code client/regulateur
   - un regulateur telecharge uniquement la page qu'il visite
   Cf docs/TECH_DEBT.md section "Performance — code splitting absent". */

// Portails par role (entry points)
const ClientPortal = lazy(() => import('./pages/ClientPortal'))
const PartnerPortal = lazy(() => import('./pages/PartnerPortal'))
const AccountantPortal = lazy(() => import('./pages/AccountantPortal'))

// Pages du Shell regulateur
const RegulationPage = lazy(() => import('./pages/RegulationPage'))
const PartnersPage = lazy(() => import('./pages/PartnersPage'))
const PartnerPage = lazy(() => import('./pages/PartnerPage'))
const ExutoiresPage = lazy(() => import('./pages/ExutoiresPage'))
const RoutesPage = lazy(() => import('./pages/RoutesPage'))
const PrelevementsPage = lazy(() => import('./pages/PrelevementsPage'))
const AvisClientsPage = lazy(() => import('./pages/AvisClientsPage'))
const Containers = lazy(() => import('./pages/Containers'))
const CataloguePage = lazy(() => import('./pages/CataloguePage'))
const FluxListPage = lazy(() => import('./pages/FluxListPage'))
const CashPage = lazy(() => import('./pages/CashPage'))
const ReglagesFacturationPage = lazy(() => import('./pages/ReglagesFacturationPage'))
const RegistreProjectionPage = lazy(() => import('./pages/RegistreProjectionPage'))
const UsersPage = lazy(() => import('./pages/UsersPage'))
const AuditsPage = lazy(() => import('./pages/AuditsPage'))
const AuditsListPage = lazy(() => import('./pages/AuditsListPage'))
const AccountPage = lazy(() => import('./pages/AccountPage'))
const AuditDetailPage = lazy(() => import('./pages/AuditDetailPage'))
const OffreComposerPage = lazy(() => import('./pages/OffreComposerPage'))
const SupplyGapsPage = lazy(() => import('./pages/SupplyGapsPage'))
const DictionnairesPage = lazy(() => import('./pages/DictionnairesPage'))
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'))
const AdminHealthPage = lazy(() => import('./pages/AdminHealthPage'))
const ProductMapPage = lazy(() => import('./pages/ProductMapPage'))
const BusinessIntelligencePage = lazy(() => import('./pages/BusinessIntelligencePage'))

// Routes publiques (rapport audit + facture + tournée chauffeur via share_token)
const AuditRapportPublic = lazy(() => import('./pages/AuditRapportPublic'))
const InvoicePublic = lazy(() => import('./pages/InvoicePublic'))
const DriverJobsPublic = lazy(() => import('./pages/DriverJobsPublic'))

/* ── Fallback commun pendant le chargement d'un chunk ── */
function RouteLoadingFallback() {
  return <PageLoader height="full" />
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,       // 30s avant refetch auto
      gcTime: 5 * 60_000,     // 5 min en cache
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
})

type UserRole = 'cinqo' | 'partner' | 'client' | 'accountant' | 'unknown'

/* ------------------------------------------------------------------ */
/*  Password reset screen                                              */
/* ------------------------------------------------------------------ */

function ResetPasswordScreen({ onDone }: { onDone: () => void }) {
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (newPassword.length < 6) {
      setError('Le mot de passe doit faire au moins 6 caracteres.')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.')
      return
    }

    setLoading(true)
    const { error: err } = await supabase.auth.updateUser({ password: newPassword })
    setLoading(false)

    if (err) {
      setError(err.message)
    } else {
      setSuccess(true)
      // Auto-redirect after 1s
      setTimeout(() => onDone(), 1000)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-violet-500 text-white text-h2 font-bold mb-4">
            C
          </div>
          <h1 className="text-h2 font-bold text-ink">Nouveau mot de passe</h1>
          <p className="text-body text-ink-muted mt-1">Choisissez votre nouveau mot de passe</p>
        </div>

        <div className="bg-surface-card rounded-xl border border-ink-ghost/30 p-6 shadow-sm">
          {success ? (
            <div className="text-center py-4">
              <div className="w-12 h-12 rounded-full bg-mint-50 flex items-center justify-center mx-auto mb-3">
                <span className="text-lg">&#10003;</span>
              </div>
              <p className="text-body font-medium text-ink mb-1">Mot de passe mis a jour</p>
              <p className="text-caption text-ink-muted mb-4">Vous pouvez maintenant utiliser votre nouveau mot de passe.</p>
              <button
                onClick={onDone}
                className="px-6 py-2.5 rounded-lg bg-violet-500 text-white text-body font-medium hover:bg-violet-600 transition-colors cursor-pointer"
              >
                Continuer
              </button>
            </div>
          ) : (
            <form onSubmit={handleReset}>
              <div className="space-y-3 mb-4">
                <div>
                  <label className="block text-data font-medium text-ink-secondary mb-1">Nouveau mot de passe</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={e => { setNewPassword(e.target.value); setError('') }}
                    placeholder="Au moins 6 caracteres"
                    required
                    minLength={6}
                    className="w-full px-4 py-2.5 rounded-lg border border-ink-ghost text-body focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500"
                  />
                </div>
                <div>
                  <label className="block text-data font-medium text-ink-secondary mb-1">Confirmer le mot de passe</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={e => { setConfirmPassword(e.target.value); setError('') }}
                    placeholder="Retapez le mot de passe"
                    required
                    minLength={6}
                    className="w-full px-4 py-2.5 rounded-lg border border-ink-ghost text-body focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500"
                  />
                </div>
              </div>

              {error && <p className="text-caption text-error mb-3">{error}</p>}

              <button
                type="submit"
                disabled={loading || !newPassword || !confirmPassword}
                className="w-full px-4 py-2.5 rounded-lg bg-violet-500 text-white text-body font-medium hover:bg-violet-600 transition-colors cursor-pointer disabled:opacity-50"
              >
                {loading ? 'Mise a jour...' : 'Enregistrer le mot de passe'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Main app content with routing                                      */
/* ------------------------------------------------------------------ */

function AppContent() {
  const { user, loading: authLoading } = useAuth()
  const [role, setRole] = useState<UserRole | null>(null)
  const [roleLoading, setRoleLoading] = useState(false)
  const [isRecovery, setIsRecovery] = useState(false)
  const recoveryHandled = useRef(false)

  // Listen for PASSWORD_RECOVERY event from Supabase
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' && !recoveryHandled.current) {
        setIsRecovery(true)
      }
    })
    return () => subscription.unsubscribe()
  }, [])

  useEffect(() => {
    if (!user) { setRole(null); return }

    // Cinqo team - no DB check needed
    if (user.email?.endsWith('@cinqo.fr')) {
      setRole('cinqo')
      return
    }

    // Check memberships (Auth V2 J1) en source primaire, entity_contacts en fallback transition.
    // Auth V2 (audit 2026-05-03) : invite-user-v2 cree user_invitations -> trigger
    // first_signin -> memberships, JAMAIS entity_contacts. Donc le source primaire pour
    // determiner le portail (client/partner) doit etre memberships, sinon les nouveaux
    // users invites depuis le 03/05 se voient afficher "Acces en attente".
    const resolve = async () => {
      setRoleLoading(true)
      try {
        const deriveRole = (c: { client_id: string | null; partner_id: string | null }): 'client' | 'partner' | 'unknown' => {
          if (c.client_id) return 'client'
          if (c.partner_id) return 'partner'
          return 'unknown'
        }

        // 1. Auth V2 : memberships (source de verite pour les invitations post-03/05)
        const { data: m } = await supabase
          .from('memberships')
          .select('client_id, partner_id, entity_type')
          .eq('auth_user_id', user.id)
          .eq('status', 'active')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle()

        if (m) { setRole(deriveRole(m)); return }

        // 2. Legacy : entity_contacts (users invites avant Auth V2 J1, transition)
        const { data: contact } = await supabase
          .from('entity_contacts')
          .select('id, client_id, partner_id')
          .eq('auth_user_id', user.id)
          .eq('can_login', true)
          .eq('is_active', true)
          .maybeSingle()

        if (contact) { setRole(deriveRole(contact)); return }

        // 3. Fallback legacy : match by email (read-only, la VIEW entity_contacts
        //    expose auth_user_id derive de memberships ; pas de self-bind possible).
        const { data: byEmail } = await supabase
          .from('entity_contacts')
          .select('id, client_id, partner_id')
          .eq('email', user.email!)
          .eq('can_login', true)
          .eq('is_active', true)
          .maybeSingle()

        if (byEmail) {
          setRole(deriveRole(byEmail)); return
        }

        // 4. Expert-comptable externe (allowlist accountant_viewers, lecture seule).
        const { data: isAccountant } = await supabase.rpc('is_accountant_viewer')
        if (isAccountant === true) { setRole('accountant'); return }

        setRole('unknown')
      } finally {
        setRoleLoading(false)
      }
    }
    resolve()
  }, [user])

  if (authLoading || roleLoading || (user && role === null)) {
    return <PageLoader height="full" />
  }

  if (!user) {
    // Auth V2 : route /admin/login dédiée staff Cinqo (Google Workspace only)
    const path = window.location.pathname
    if (path === '/admin/login' || path.startsWith('/admin/login')) {
      return <LoginPageStaff />
    }
    return <LoginPage />
  }

  // Show password reset form if user arrived via recovery link
  if (isRecovery) {
    return <ResetPasswordScreen onDone={() => { recoveryHandled.current = true; setIsRecovery(false) }} />
  }

  if (role === 'cinqo') return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <Routes>
        <Route element={<Shell />}>
          <Route index element={<Navigate to="/operations" replace />} />
          <Route path="operations" element={<RegulationPage />} />
          <Route path="flux" element={<FluxListPage />} />
          {/* Ancienne liste par offre : hors menu, joignable par URL le temps du nouveau builder. */}
          <Route path="flux/offres" element={<Navigate to="/flux" replace />} />
          <Route path="flux/lignes" element={<Navigate to="/flux?vue=lignes" replace />} />
          <Route path="flux/nouveau" element={<OffreComposerPage />} />
          <Route path="flux/nouvelle-offre" element={<Navigate to="/flux/nouveau" replace />} />
          <Route path="flux/offre/:offerKey" element={<OffreComposerPage />} />
          {/* Hub Facturation (funnel continu : à facturer → factures → encaissement → virements → réglages). */}
          <Route path="facturation" element={<CashPage />} />
          <Route path="prelevements" element={<PrelevementsPage />} />
          <Route path="avis-clients" element={<AvisClientsPage />} />
          <Route path="business-intelligence" element={<BusinessIntelligencePage />} />
          <Route path="cash" element={<CashPage />} />
          <Route path="reglages-facturation" element={<ReglagesFacturationPage />} />
          <Route path="registre" element={<RegistreProjectionPage />} />
          <Route path="documents" element={<DocumentsPage mode="regulateur" />} />
          <Route path="clients" element={<Navigate to="/comptes" replace />} />
          <Route path="partners" element={<PartnersPage />} />
          {/* Page partenaire « record page » : liste Partenaires → /partners/:partnerId */}
          <Route path="partners/:partnerId" element={<PartnerPage />} />
          <Route path="containers" element={<Containers />} />
          <Route path="catalogue" element={<CataloguePage mode="regulateur" />} />
          <Route path="exutoires" element={<ExutoiresPage />} />
          <Route path="routes" element={<RoutesPage />} />
          <Route path="dictionnaires" element={<DictionnairesPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="users-v2" element={<Navigate to="/users" replace />} />
          <Route path="audits" element={<AuditsListPage />} />
          {/* Page compte « record page » : liste Comptes → /comptes/:clientId */}
          <Route path="comptes" element={<AuditsPage />} />
          <Route path="comptes/:clientId" element={<AccountPage />} />
          <Route path="audits/:id" element={<AuditDetailPage />} />

          <Route path="supply-gaps" element={<SupplyGapsPage />} />
          <Route path="admin/health" element={<AdminHealthPage />} />
          <Route path="dev/e2e" element={<ProductMapPage />} />
          <Route path="*" element={<Navigate to="/operations" replace />} />
        </Route>
      </Routes>
    </Suspense>
  )
  if (role === 'partner') return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <PartnerPortal />
    </Suspense>
  )
  if (role === 'client') return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <ClientPortal />
    </Suspense>
  )
  if (role === 'accountant') return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <AccountantPortal />
    </Suspense>
  )

  // Unknown role - show access pending
  return (
    <div className="min-h-screen flex items-center justify-center bg-surface">
      <div className="bg-surface-card rounded-xl border border-ink-ghost/30 p-8 max-w-md text-center">
        <div className="w-12 h-12 rounded-full bg-warning-bg flex items-center justify-center mx-auto mb-4">
          <span className="text-2xl">&#9888;</span>
        </div>
        <h2 className="text-lg font-bold text-ink mb-2">Acces en attente</h2>
        <p className="text-sm text-ink-muted mb-4">
          Votre compte ({user.email}) n'est pas encore rattache a un client ou un partenaire.
          Contactez l'equipe Cinqo pour configurer votre acces.
        </p>
        <button
          onClick={() => supabase.auth.signOut()}
          className="px-4 py-2 rounded-lg bg-surface-mid text-ink-secondary text-sm font-medium hover:bg-ink-ghost transition-colors cursor-pointer"
        >
          Se deconnecter
        </button>
      </div>
    </div>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        {/* Banner "Recharger" quand un nouveau deploy est detecte (polling
            60s sur /build-id.json). Monte au root = couvre tous les portails
            (regulateur, client, partner) + les routes publiques /rapport
            et /facture. Skip en dev. Cf lib/useBuildVersionCheck. */}
        <NewVersionBanner />
        <Routes>
          {/* Routes publiques — pas d'auth */}
          <Route
            path="/rapport/:token"
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <AuditRapportPublic />
              </Suspense>
            }
          />
          <Route
            path="/facture/:token"
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <InvoicePublic />
              </Suspense>
            }
          />
          <Route
            path="/chauffeur/:token"
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <DriverJobsPublic />
              </Suspense>
            }
          />
          {/* Tout le reste passe par l'auth */}
          <Route path="*" element={<AuthProvider><AppContent /></AuthProvider>} />
        </Routes>
        {/* Viewer PDF in-app (drawer global). Monte au root = tous les
            portails ; openInvoicePdf s'y rattache via lib/pdfViewer. */}
        <PdfViewerHost />
      </QueryClientProvider>
    </BrowserRouter>
  )
}
